import { useState } from 'react'
import { CheckCircle, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatTime } from '@/lib/dates'
import type {
  Note,
  NoteApproval,
  Addendum,
  DocumentationAction,
} from '@/types/documentation'

interface ClinicalNoteEditorProps {
  notes: Note[]
  selectedNote: Note | null
  noteContent: string
  noteApproval: NoteApproval
  addendums: Addendum[]
  dispatch: React.Dispatch<DocumentationAction>
  providerName: string
}

export default function ClinicalNoteEditor({
  notes,
  selectedNote,
  noteContent,
  noteApproval,
  addendums,
  dispatch,
  providerName,
}: ClinicalNoteEditorProps) {
  const [addendumText, setAddendumText] = useState('')
  const [showAddendum, setShowAddendum] = useState(false)

  const handleNoteSelect = (noteId: string) => {
    const note = notes.find((n) => n.id === noteId)
    if (note) {
      dispatch({ type: 'SELECT_NOTE', note })
    }
  }

  const handleApprove = () => {
    dispatch({ type: 'APPROVE_NOTE', approvedBy: providerName, approvedAt: new Date().toISOString() })
  }

  const handleAddAddendum = () => {
    if (addendumText.trim()) {
      dispatch({
        type: 'ADD_ADDENDUM',
        content: addendumText.trim(),
        author: providerName,
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
      })
      setAddendumText('')
      setShowAddendum(false)
    }
  }

  if (!selectedNote && notes.length === 0) {
    return (
      <div className="rounded-lg border bg-white p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">
          Clinical Note
        </h3>
        <p className="text-sm text-slate-500">
          Attach a recording to populate the clinical note.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border bg-white p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Clinical Note</h3>
        {noteApproval.approved && (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        )}
      </div>

      {notes.length > 1 && (
        <Select
          value={selectedNote?.id ?? ''}
          onValueChange={handleNoteSelect}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a note template" />
          </SelectTrigger>
          <SelectContent>
            {notes.map((note) => (
              <SelectItem key={note.id} value={note.id}>
                {note.template_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {noteApproval.approved ? (
        <div className="rounded-md bg-slate-50 p-4">
          <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
            {noteContent}
          </pre>
        </div>
      ) : (
        <Textarea
          value={noteContent}
          onChange={(e) =>
            dispatch({ type: 'UPDATE_NOTE_CONTENT', content: e.target.value })
          }
          placeholder="Clinical note content..."
          aria-label="Clinical note content"
          className="min-h-[300px] font-mono text-sm"
        />
      )}

      {!noteApproval.approved && noteContent.trim() && (
        <Button onClick={handleApprove} size="sm">
          <CheckCircle className="h-4 w-4 mr-1" />
          Approve Note
        </Button>
      )}

      {noteApproval.approved && (
        <div className="space-y-3 border-t pt-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Approved by {noteApproval.approved_by} at{' '}
              {noteApproval.approved_at && formatTime(noteApproval.approved_at)}
            </p>
          </div>

          {addendums.map((addendum) => (
            <div
              key={addendum.id}
              className="rounded-md border border-amber-200 bg-amber-50 p-3"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-amber-800">
                  Addendum
                </span>
                <span className="text-xs text-amber-600">
                  {formatTime(addendum.created_at)}{' '}
                  — {addendum.author}
                </span>
              </div>
              <p className="text-sm text-amber-900">{addendum.content}</p>
            </div>
          ))}

          {showAddendum ? (
            <div className="space-y-2">
              <Textarea
                value={addendumText}
                onChange={(e) => setAddendumText(e.target.value)}
                placeholder="Enter addendum..."
                aria-label="Addendum text"
                className="min-h-[80px] text-sm"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddAddendum} disabled={!addendumText.trim()}>
                  Save Addendum
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowAddendum(false)
                    setAddendumText('')
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddendum(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Addendum
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
